<template>
    <LoadingComponent :props="loading" />

    <div id="otp" class="db-card db-tab-div active" :class="{ 'on-update': onUpdateActive }">
        <div class="db-card-header items-center justify-between gap-4">
            <h3 class="db-card-title">{{ $t("menu.otp") }}</h3>
        
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="db-btn h-9 py-2 px-3 text-white bg-primary ml-3"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div>
        </div>
        <div class="db-card-body">
            <form @submit.prevent="save" class="w-full d-block">
                <div class="form-row">
                    <!-- VERIFICACIÓN OTP -->
                    <div class="form-col-12 pb-3 mb-2">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">{{ $t('label.otp_verification_title') }}</h4>
                        <p class="text-xs text-gray-500 mb-4">{{ $t('label.otp_verification_description') }}</p>
                    </div>

                    <div class="form-col-12 sm:form-col-6">
                        <label for="otp_type" class="db-field-title required">{{ $t("label.otp_type") }}</label>
                        <vue-select class="db-field-control f-b-custom-select" id="otp_type"
                            v-bind:class="errors.otp_type ? 'invalid' : ''" v-model="form.otp_type"
                            :options="enums.otpTypeEnum" label-by="name" value-by="id" :closeOnSelect="true"
                            :searchable="true" :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.otp_type">{{ errors.otp_type[0] }}</small>
                    </div>
                    <div class="form-col-12 sm:form-col-6">
                        <label for="otp_digit_limit" class="db-field-title required">{{
                            $t("label.otp_digit_limit")
                        }}</label>

                        <vue-select class="db-field-control f-b-custom-select" id="otp_digit_limit"
                            v-bind:class="errors.otp_digit_limit ? 'invalid' : ''" v-model="form.otp_digit_limit"
                            :options="enums.otpDigitLimitEnum" label-by="name" value-by="id" :closeOnSelect="true"
                            :searchable="true" :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.otp_digit_limit">{{
                            errors.otp_digit_limit[0]
                        }}</small>
                    </div>
                    <div class="form-col-12 sm:form-col-6">
                        <label for="otp_expire_time" class="db-field-title required">{{
                            $t("label.otp_expire_time")
                        }}</label>

                        <vue-select class="db-field-control f-b-custom-select" id="otp_expire_time"
                            v-bind:class="errors.otp_expire_time ? 'invalid' : ''" v-model="form.otp_expire_time"
                            :options="enums.otpExpireTimeEnum" label-by="name" value-by="id" :closeOnSelect="true"
                            :searchable="true" :clearOnClose="true" placeholder="--" search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.otp_expire_time">{{
                            errors.otp_expire_time[0]
                        }}</small>
                    </div>

                    <!-- RECAPTCHA DE GOOGLE -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">{{ $t('label.recaptcha_title') }}</h4>
                        <p class="text-xs text-gray-500 mb-4">{{ $t('label.recaptcha_description') }}</p>
                    </div>

                    <!-- Plugin Master Switch -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.recaptcha_status') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.recaptcha_status" id="recaptcha_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="recaptcha_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.recaptcha_status" id="recaptcha_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="recaptcha_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.recaptcha_status">{{ errors.recaptcha_status[0] }}</small>
                    </div>

                    <!-- Switch Login Normal -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.recaptcha_manual_login') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.recaptcha_login_enabled" id="recaptcha_login_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="recaptcha_login_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.recaptcha_login_enabled" id="recaptcha_login_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="recaptcha_login_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.recaptcha_login_enabled">{{ errors.recaptcha_login_enabled[0] }}</small>
                    </div>

                    <!-- Site Key -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="recaptcha_site_key" class="db-field-title">{{ $t('label.recaptcha_site_key') }}</label>
                        <input v-model="form.recaptcha_site_key" type="text" id="recaptcha_site_key" class="db-field-control font-mono text-xs" placeholder="Ej. 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" />
                        <small class="db-field-alert" v-if="errors.recaptcha_site_key">{{ errors.recaptcha_site_key[0] }}</small>
                    </div>

                    <!-- Secret Key -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="recaptcha_secret_key" class="db-field-title">{{ $t('label.recaptcha_secret_key') }}</label>
                        <input v-model="form.recaptcha_secret_key" type="password" id="recaptcha_secret_key" class="db-field-control font-mono text-xs" placeholder="Ej. 6LeIxAcTAAAAAGG-vFI1TnRWxMZNFuojJ4WifJWe" />
                        <small class="db-field-alert" v-if="errors.recaptcha_secret_key">{{ errors.recaptcha_secret_key[0] }}</small>
                    </div>

                    <!-- AUTENTICACIÓN DE 2 FACTORES (2FA) -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">{{ $t('label.two_factor_title') }}</h4>
                        <p class="text-xs text-gray-500 mb-4">{{ $t('label.two_factor_description') }}</p>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.two_factor_status') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.two_factor_status" id="two_factor_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="two_factor_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.two_factor_status" id="two_factor_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="two_factor_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.two_factor_status">{{ errors.two_factor_status[0] }}</small>
                    </div>

                    <!-- INICIO DE SESIÓN SOCIAL (SSO) -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">{{ $t('label.sso_title') }}</h4>
                        <p class="text-xs text-gray-500 mb-4">{{ $t('label.sso_description') }}</p>
                    </div>

                    <!-- Master SSO Switch -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.sso_status') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.social_login_status" id="social_login_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="social_login_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.social_login_status" id="social_login_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="social_login_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.social_login_status">{{ errors.social_login_status[0] }}</small>
                    </div>

                    <!-- Google SSO Switch -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.google_sso') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.google_login_status" id="google_login_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="google_login_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.google_login_status" id="google_login_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="google_login_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.google_login_status">{{ errors.google_login_status[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="google_client_id" class="db-field-title">{{ $t('label.google_client_id') }}</label>
                        <input v-model="form.google_client_id" type="text" id="google_client_id" class="db-field-control font-mono text-xs" placeholder="xxx.apps.googleusercontent.com" />
                        <small class="db-field-alert" v-if="errors.google_client_id">{{ errors.google_client_id[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="google_client_secret" class="db-field-title">{{ $t('label.google_client_secret') }}</label>
                        <input v-model="form.google_client_secret" type="password" id="google_client_secret" class="db-field-control font-mono text-xs" placeholder="GOCSPX-xxx" />
                        <small class="db-field-alert" v-if="errors.google_client_secret">{{ errors.google_client_secret[0] }}</small>
                    </div>

                    <!-- Facebook SSO Switch -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title required">{{ $t('label.facebook_sso') }}</label>
                        <div class="db-field-radio-group">
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="1" v-model.number="form.facebook_login_status" id="facebook_login_enable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="facebook_login_enable" class="db-field-label">{{ $t("label.enable") }}</label>
                            </div>
                            <div class="db-field-radio">
                                <div class="custom-radio">
                                    <input :value="0" v-model.number="form.facebook_login_status" id="facebook_login_disable" type="radio" class="custom-radio-field" />
                                    <span class="custom-radio-span"></span>
                                </div>
                                <label for="facebook_login_disable" class="db-field-label">{{ $t("label.disable") }}</label>
                            </div>
                        </div>
                        <small class="db-field-alert" v-if="errors.facebook_login_status">{{ errors.facebook_login_status[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="facebook_client_id" class="db-field-title">{{ $t('label.facebook_client_id') }}</label>
                        <input v-model="form.facebook_client_id" type="text" id="facebook_client_id" class="db-field-control font-mono text-xs" placeholder="Ej. 1234567890" />
                        <small class="db-field-alert" v-if="errors.facebook_client_id">{{ errors.facebook_client_id[0] }}</small>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label for="facebook_client_secret" class="db-field-title">{{ $t('label.facebook_client_secret') }}</label>
                        <input v-model="form.facebook_client_secret" type="password" id="facebook_client_secret" class="db-field-control font-mono text-xs" placeholder="Ej. 4a5b6c7d..." />
                        <small class="db-field-alert" v-if="errors.facebook_client_secret">{{ errors.facebook_client_secret[0] }}</small>
                    </div>

                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoadingComponent from "../../components/LoadingComponent";
import alertService from "../../../../services/alertService";
import otpTypeEnum from "../../../../enums/modules/otpTypeEnum";
import otpDigitLimitEnum from "../../../../enums/modules/otpDigitLimitEnum";
import otpExpireTimeEnum from "../../../../enums/modules/otpExpireTimeEnum";

export default {
    name: "OtpComponent",
    components: { LoadingComponent },
    data() {
        return {
            isRefreshingView: false,
            loading: {
                isActive: false,
            },
            form: {
                otp_type: null,
                otp_digit_limit: null,
                otp_expire_time: null,
                recaptcha_status: 0,
                recaptcha_site_key: "",
                recaptcha_secret_key: "",
                recaptcha_login_enabled: 0,
                two_factor_status: 0,
                social_login_status: 0,
                google_login_status: 0,
                google_client_id: "",
                google_client_secret: "",
                facebook_login_status: 0,
                facebook_client_id: "",
                facebook_client_secret: "",
            },
            enums: {
                otpTypeEnum: otpTypeEnum,
                otpDigitLimitEnum: otpDigitLimitEnum,
                otpExpireTimeEnum: otpExpireTimeEnum,
            },
            isSaving: false,
            onUpdateActive: false,
            errors: {},
        };
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        fetchData: function (showOverlay = true) {
            if (showOverlay) {
                this.loading.isActive = true;
            }
            return this.$store
                .dispatch("otp/lists")
                .then((res) => {
                    if (res && res.data && res.data.data) {
                        Object.assign(this.form, res.data.data);
                    }
                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                })
                .catch((err) => {
                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                });
        },
        refreshFromServer: function () {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.onUpdateActive = true;
            this.fetchData(false).then(() => {
                alertService.info(this.$t('button.refresh'));
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            }).catch(() => {
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            });
        },
        save: function () {
            try {
                this.isSaving = true;
                this.onUpdateActive = true;
                this.$store.dispatch("otp/save", this.form).then((res) => {
                    alertService.successFlip(res.config.method === "put" ?? 0, this.$t("menu.otp"));
                    this.$store.dispatch("otp/lists").then((response) => {
                        this.form.otp_type = response.data.data.otp_type;
                        this.form.otp_digit_limit = response.data.data.otp_digit_limit;
                        this.form.otp_expire_time = response.data.data.otp_expire_time;
                    }).catch(() => {
                        // Ignore refresh errors after successful save.
                    });
                    this.errors = {};
                    this.isSaving = false;
                    this.onUpdateActive = false;
                }).catch((err) => {
                    this.errors = err.response.data.errors;
                    this.isSaving = false;
                    this.onUpdateActive = false;
                });
            } catch (err) {
                this.isSaving = false;
                this.onUpdateActive = false;
                alertService.error(err);
            }
        },
    },
};
</script>
